"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { validateAddressShape } from "@/lib/strkey";
import type { NetworkKey } from "@/lib/api/positions";

/**
 * Wallet Explorer landing.
 *
 * Editorial two-column. Left column establishes what the tool is and what
 * it covers. Right column is the input panel. Below the fold: a live
 * status strip that pulls counts from the data-api and a small "Recently
 * inspected" history from localStorage. No auth, no data fetch on submit.
 */
export default function WalletExplorerLanding() {
  const router = useRouter();
  const [input, setInput] = useState("");
  const [network, setNetwork] = useState<NetworkKey>("mainnet");
  const [error, setError] = useState<string | null>(null);
  const [coverage, setCoverage] = useState<CoverageStats | null>(null);
  const [recent, setRecent] = useState<RecentLookup[]>([]);

  useEffect(() => {
    let cancelled = false;
    void loadCoverage().then((c) => { if (!cancelled) setCoverage(c); });
    setRecent(loadRecent());
    return () => { cancelled = true; };
  }, []);

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const result = validateAddressShape(input);
    if (!result.ok) {
      setError(result.reason);
      return;
    }
    setError(null);
    saveRecent({ address: result.address, network, ts: Date.now() });
    const qs = network === "mainnet" ? "" : `?network=${network}`;
    router.push(`/explorer/wallets/${result.address}${qs}`);
  }

  return (
    <main className="min-h-screen" style={{ background: "var(--bg)", color: "var(--text-primary)" }}>
      <section className="relative px-6 sm:px-10 pt-24 pb-16">
        <div className="mx-auto" style={{ maxWidth: 1100 }}>
          {/* Section eyebrow */}
          <p
            className="text-[10px] tracking-[0.18em] uppercase mb-10"
            style={{ color: "var(--text-secondary)" }}
          >
            <span
              className="inline-block w-1.5 h-1.5 rounded-full mr-2 align-[1px]"
              style={{ background: "var(--accent)", boxShadow: "0 0 8px rgba(200,255,94,0.6)" }}
            />
            Wallet Explorer · Hoops Finance
          </p>

          {/* Two-column hero */}
          <div className="grid items-start gap-12 md:gap-16 hoops-explorer-hero">
            {/* Left: editorial */}
            <div>
              <h1
                style={{
                  fontFamily: "var(--font-instrument-serif), Georgia, serif",
                  fontStyle: "italic",
                  fontSize: "clamp(40px, 5.5vw, 60px)",
                  color: "#f0ece4",
                  letterSpacing: "-0.025em",
                  fontWeight: 400,
                  lineHeight: 1.02,
                  margin: 0,
                }}
              >
                Every position,
                <br />
                one address.
              </h1>

              <p
                className="text-[14px] mt-6"
                style={{ color: "var(--text-primary)", lineHeight: 1.55, maxWidth: 460 }}
              >
                A read-only view of any Stellar wallet&apos;s holdings — classic
                balances, liquidity positions, lending, open offers — priced
                live from our self-hosted Horizon and Soroban indexer.
              </p>

              <p
                className="text-[12px] mt-4"
                style={{ color: "var(--text-secondary)", lineHeight: 1.6, maxWidth: 460 }}
              >
                Built for funds and analysts who need their NAV in one call. No
                connection required, no wallet popup, no signature.
              </p>

              {/* Protocol coverage strip */}
              <ul
                className="grid gap-x-6 gap-y-2 mt-10 m-0 p-0"
                style={{
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  listStyle: "none",
                  maxWidth: 460,
                }}
              >
                {COVERAGE_LIST.map((c) => (
                  <li
                    key={c.label}
                    className="flex items-center gap-2 text-[11px]"
                    style={{ color: "var(--text-secondary)" }}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: 999,
                        background: c.ready ? "var(--accent)" : "var(--text-tertiary)",
                        flexShrink: 0,
                      }}
                    />
                    <span
                      style={{
                        color: c.ready ? "var(--text-primary)" : "var(--text-secondary)",
                      }}
                    >
                      {c.label}
                    </span>
                    <span
                      className="text-[9px] tracking-[0.1em] uppercase"
                      style={{ color: "var(--text-tertiary)" }}
                    >
                      {c.ready ? "live" : "next"}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right: input panel */}
            <aside>
              <div
                className="rounded-xl"
                style={{
                  background: "var(--surface)",
                  border: "1px solid var(--border-default)",
                  padding: 18,
                }}
              >
                <p
                  className="text-[9px] tracking-[0.14em] uppercase"
                  style={{ color: "var(--text-secondary)" }}
                >
                  Inspect an address
                </p>
                <form onSubmit={onSubmit} className="mt-3">
                  <label
                    className="block text-[10px] tracking-[0.06em] uppercase mb-1.5"
                    style={{ color: "var(--text-secondary)" }}
                    htmlFor="address-input"
                  >
                    Stellar address
                  </label>
                  <input
                    id="address-input"
                    type="text"
                    value={input}
                    onChange={(e) => { setInput(e.target.value); if (error) setError(null); }}
                    placeholder="GCKFBEIYTKP6RJGWLOUQBCGW… or CASDSU3PBSFYJ4ZX7…"
                    spellCheck={false}
                    autoCapitalize="characters"
                    autoComplete="off"
                    className="w-full bg-transparent outline-none px-2.5 py-2 text-[12px] rounded-md"
                    style={{
                      color: "var(--text-primary)",
                      border: error
                        ? "1px solid #e05252"
                        : "1px solid var(--border-default)",
                      fontFamily: "var(--font-geist-mono), ui-monospace, monospace",
                    }}
                    aria-invalid={Boolean(error)}
                  />
                  {error && (
                    <p
                      className="text-[11px] mt-1.5"
                      role="alert"
                      style={{ color: "#e05252" }}
                    >
                      {error}
                    </p>
                  )}

                  <div
                    className="grid items-end gap-3 mt-4"
                    style={{ gridTemplateColumns: "1fr auto" }}
                  >
                    <div>
                      <label
                        className="block text-[10px] tracking-[0.06em] uppercase mb-1.5"
                        style={{ color: "var(--text-secondary)" }}
                      >
                        Network
                      </label>
                      <div
                        className="inline-flex items-center gap-0.5 rounded-md p-0.5 w-full"
                        style={{ border: "1px solid var(--border-default)" }}
                        role="radiogroup"
                        aria-label="Network"
                      >
                        {(["mainnet", "testnet"] as const).map((n) => (
                          <button
                            key={n}
                            type="button"
                            role="radio"
                            aria-checked={network === n}
                            onClick={() => { setNetwork(n); }}
                            className="flex-1 text-[11px] py-1.5 rounded-[5px] transition-colors duration-150"
                            style={{
                              background: network === n ? "var(--surface-hi)" : "transparent",
                              color: network === n ? "var(--text-primary)" : "var(--text-secondary)",
                              fontWeight: network === n ? 500 : 400,
                            }}
                          >
                            {n.charAt(0).toUpperCase() + n.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>

                    <button
                      type="submit"
                      className="text-[11px] tracking-[0.04em] font-medium px-4 py-2 rounded-md transition-colors duration-150"
                      style={{
                        background: "var(--accent)",
                        color: "#0a0a0a",
                        height: 34,
                      }}
                    >
                      Inspect →
                    </button>
                  </div>
                </form>

                {recent.length > 0 && (
                  <>
                    <div
                      className="h-px my-4"
                      style={{ background: "var(--border-subtle)" }}
                    />
                    <p
                      className="text-[9px] tracking-[0.14em] uppercase mb-2"
                      style={{ color: "var(--text-secondary)" }}
                    >
                      Recently inspected
                    </p>
                    <ul
                      className="m-0 p-0 space-y-1"
                      style={{ listStyle: "none" }}
                    >
                      {recent.slice(0, 4).map((r) => (
                        <li key={r.address + String(r.ts)}>
                          <button
                            type="button"
                            onClick={() => { setInput(r.address); setNetwork(r.network); }}
                            className="w-full text-left flex items-center justify-between gap-2 px-2 py-1.5 rounded-md transition-colors duration-150 hover:bg-[var(--surface-hi)]"
                          >
                            <span
                              className="text-[11px] truncate"
                              style={{
                                color: "var(--text-secondary)",
                                fontFamily:
                                  "var(--font-geist-mono), ui-monospace, monospace",
                              }}
                            >
                              {truncate(r.address)}
                            </span>
                            <span
                              className="text-[9px] tracking-[0.08em] uppercase"
                              style={{
                                color:
                                  r.network === "mainnet"
                                    ? "var(--text-tertiary)"
                                    : "var(--accent)",
                              }}
                            >
                              {r.network}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>

              <p
                className="text-[10px] mt-3 text-center"
                style={{ color: "var(--text-tertiary)" }}
              >
                Public · No login · No signature
              </p>
            </aside>
          </div>

          {/* Live coverage strip */}
          <div
            className="rounded-xl overflow-hidden grid mt-12 md:mt-16"
            style={{
              background: "var(--surface)",
              border: "1px solid var(--border-default)",
              gridTemplateColumns: "repeat(4, 1fr)",
            }}
          >
            <Stat label="Indexed pools" value={coverage?.pools ?? null} loading={!coverage} />
            <Stat label="Tracked tokens" value={coverage?.tokens ?? null} loading={!coverage} />
            <Stat label="Protocols" value={coverage?.protocols ?? null} loading={!coverage} />
            <Stat
              label="Last update"
              value={coverage?.updatedLabel ?? null}
              loading={!coverage}
              pulse
              noBorder
            />
          </div>

          {/* Footer note */}
          <p
            className="text-[11px] mt-8"
            style={{ color: "var(--text-secondary)", maxWidth: 720, lineHeight: 1.5 }}
          >
            <span
              className="text-[9px] tracking-[0.14em] uppercase mr-2"
              style={{ color: "var(--text-tertiary)" }}
            >
              v1 status
            </span>
            Classic G-account balances, SAC C-account balances, and LP unwrap
            (Soroswap / Phoenix / Comet / Aqua) are live across mainnet with
            pro-rata underlying breakdown and USD pricing on each leg. Blend
            lending positions, SDEX open offers, and NAV-over-time history are
            the next-up additions.
          </p>
        </div>
      </section>

      {/* Hero responsive grid — desktop two-column, mobile single column */}
      <style>{`
        .hoops-explorer-hero { grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr); }
        @media (max-width: 760px) {
          .hoops-explorer-hero { grid-template-columns: minmax(0, 1fr); }
        }
        @keyframes hoops-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.35; }
        }
      `}</style>
    </main>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// helpers + types
// ──────────────────────────────────────────────────────────────────────────

const COVERAGE_LIST: { label: string; ready: boolean }[] = [
  { label: "Classic accounts (G…)", ready: true },
  { label: "Trustline assets", ready: true },
  { label: "USD pricing", ready: true },
  { label: "SDEX classic", ready: true },
  { label: "Soroban contracts (C…)", ready: true },
  { label: "LP unwrap (Soroswap / Phoenix / Comet / Aqua)", ready: true },
  { label: "Blend lending positions", ready: false },
  { label: "SDEX open offers", ready: false },
  { label: "NAV history chart", ready: false },
];

interface CoverageStats {
  pools: string;
  tokens: string;
  protocols: string;
  updatedLabel: string;
}

interface RecentLookup {
  address: string;
  network: NetworkKey;
  ts: number;
}

function Stat({
  label,
  value,
  loading,
  pulse = false,
  noBorder = false,
}: {
  label: string;
  value: string | null;
  loading: boolean;
  pulse?: boolean;
  noBorder?: boolean;
}) {
  return (
    <div
      className="px-5 py-4"
      style={noBorder ? undefined : { borderRight: "1px solid var(--border-subtle)" }}
    >
      <p
        className="text-[9px] tracking-[0.14em] uppercase"
        style={{ color: "var(--text-secondary)" }}
      >
        {label}
      </p>
      <div
        className="mt-1.5 flex items-center gap-2"
        style={{
          fontFamily: "var(--font-instrument-serif), Georgia, serif",
          fontStyle: "italic",
          fontSize: 22,
          color: "var(--text-primary)",
          lineHeight: 1.1,
        }}
      >
        {loading ? (
          <span
            className="inline-block rounded animate-pulse"
            style={{ width: 64, height: 18, background: "var(--surface-hi)" }}
            aria-hidden="true"
          />
        ) : (
          <>
            {pulse && (
              <span
                aria-hidden="true"
                className="inline-block w-1.5 h-1.5 rounded-full"
                style={{
                  background: "var(--accent)",
                  boxShadow: "0 0 8px rgba(200,255,94,0.6)",
                  animation: "hoops-pulse 2.4s ease-in-out infinite",
                }}
              />
            )}
            <span>{value}</span>
          </>
        )}
      </div>
    </div>
  );
}

async function loadCoverage(): Promise<CoverageStats> {
  const base = process.env.NEXT_PUBLIC_BASE_DATA_URI ?? "https://api-dev.hoops.fi";
  try {
    const [pairsRes, tokensRes, protocolsRes] = await Promise.all([
      fetch(`${base}/pairs`, { signal: AbortSignal.timeout(6_000) }),
      fetch(`${base}/tokens`, { signal: AbortSignal.timeout(6_000) }),
      fetch(`${base}/protocols`, { signal: AbortSignal.timeout(6_000) }),
    ]);
    const pairs = (await pairsRes.json()) as { lastUpdated?: string }[];
    const tokens = (await tokensRes.json()) as unknown[];
    const protocolsBody = (await protocolsRes.json()) as { protocols?: unknown[] } | unknown[];
    const protocols = Array.isArray(protocolsBody)
      ? protocolsBody
      : (protocolsBody.protocols ?? []);

    const newest = pairs.reduce<number>((acc, p) => {
      const t = p.lastUpdated ? Date.parse(p.lastUpdated) : 0;
      return t > acc ? t : acc;
    }, 0);

    return {
      pools: pairs.length.toLocaleString("en-US"),
      tokens: tokens.length.toLocaleString("en-US"),
      protocols: String(protocols.length),
      updatedLabel: newest ? relativeTime(newest) : "—",
    };
  } catch {
    return { pools: "—", tokens: "—", protocols: "—", updatedLabel: "—" };
  }
}

function relativeTime(ts: number): string {
  const delta = Math.max(1, Math.floor((Date.now() - ts) / 1000));
  if (delta < 60) return `${String(delta)}s ago`;
  if (delta < 3600) return `${String(Math.floor(delta / 60))}m ago`;
  if (delta < 86400) return `${String(Math.floor(delta / 3600))}h ago`;
  return `${String(Math.floor(delta / 86400))}d ago`;
}

function truncate(addr: string): string {
  if (addr.length <= 14) return addr;
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

const RECENT_KEY = "hoops:explorer:recent";

function loadRecent(): RecentLookup[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is RecentLookup =>
        typeof e === "object" &&
        e !== null &&
        typeof (e as RecentLookup).address === "string" &&
        ((e as RecentLookup).network === "mainnet" ||
          (e as RecentLookup).network === "testnet"),
    );
  } catch {
    return [];
  }
}

function saveRecent(entry: RecentLookup): void {
  if (typeof window === "undefined") return;
  try {
    const list = loadRecent().filter(
      (r) => !(r.address === entry.address && r.network === entry.network),
    );
    list.unshift(entry);
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 6)));
  } catch {
    /* ignore quota errors */
  }
}
